import { useOrganization } from "./use-organization";
import { Permission } from "@api/shared-runtime/enums";

export { Permission };

/**
 * Hook to check BU-scoped role permissions.
 * Uses the current organization context to get the selected BU's permissions.
 * Admins automatically have all permissions.
 *
 * Must be used within an OrgLayout (organization context).
 */
export function useRolePermissions(
  user: { is_admin: boolean } | null | undefined,
) {
  const { selectedOrgBusinessUnit } = useOrganization();

  const hasPermission = (permission: Permission): boolean => {
    if (!user) {
      return false;
    }
    if (user.is_admin) {
      return true;
    }
    return selectedOrgBusinessUnit.permissions?.includes(permission) ?? false;
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!user) {
      return false;
    }
    if (user.is_admin) {
      return true;
    }
    return permissions.some(
      (p) => selectedOrgBusinessUnit.permissions?.includes(p) ?? false,
    );
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    if (!user) {
      return false;
    }
    if (user.is_admin) {
      return true;
    }
    return permissions.every(
      (p) => selectedOrgBusinessUnit.permissions?.includes(p) ?? false,
    );
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}
